import { useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { CodeNameModel, FileList } from 'components/common/types';
import { SortDir, SortSettings } from 'components/common/types/SortDir.types';
import { getIsReseller } from 'store/publisher/selectors';
import { UseListOfFilesStateResult } from './ListOfFiles.types';

const ITEM_HEIGHT = 44;
const RESELLER_ITEM_HEIGHT = 64;

export const orderByField = <T extends CodeNameModel, K extends keyof T>(
  items: T[],
  field: K,
  direction: SortDir,
): T[] => {
  if (direction === SortDir.NONE) return items;

  return items.sort((a, b) => {
    const aValue = String(a[field]);
    const bValue = String(b[field]);

    return direction === SortDir.ASC
      ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
      : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
  });
};

export const useListOfFilesState = (uploadedListOfFiles: FileList[]): UseListOfFilesStateResult => {
  const isReseller = useSelector(getIsReseller);

  const [sortDirection, setSortDirection] = useState(SortDir.ASC);
  const [currentUploadedListOfFiles, setCurrentUploadedListOfFiles] = useState(uploadedListOfFiles);

  const itemHeight = isReseller ? RESELLER_ITEM_HEIGHT : ITEM_HEIGHT;

  useEffect(() => {
    setCurrentUploadedListOfFiles(uploadedListOfFiles);
  }, [uploadedListOfFiles]);

  const onCategoryDirectionSort = (fileName: string, direction: SortDir): void => {
    const uploadedFiles = cloneDeep(currentUploadedListOfFiles);

    setSortDirection(SortSettings[sortDirection].nextDir);

    const fileIndex = uploadedFiles.findIndex(({ name }) => name === fileName);
    const contents = uploadedFiles[fileIndex].fileContents;

    if (!contents) return;

    const sorted = orderByField(contents, 'name', direction);

    uploadedFiles[fileIndex].fileContents = sorted;

    setCurrentUploadedListOfFiles(uploadedFiles);
  };

  return {
    currentUploadedListOfFiles,
    sortDirection,
    itemHeight,
    onCategoryDirectionSort,
  };
};
